import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { DollarSign, Store, Users, Utensils, TrendingUp, BadgeCheck, XCircle, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [owners, setOwners] = useState([]);
  const [events, setEvents] = useState([]);
  const [running, setRunning] = useState(false);

  const load = () => {
    Promise.all([api.get("/admin/stats"), api.get("/admin/owners"), api.get("/admin/events")])
      .then(([s, o, e]) => { setStats(s.data); setOwners(o.data); setEvents(e.data); })
      .catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const runDunning = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/run-dunning");
      toast.success(`Dunning ran. Past-due members: ${data.past_due_after}`);
      load();
    } catch { toast.error("Failed to run dunning"); }
    finally { setRunning(false); }
  };

  return (
    <div className="App min-h-screen bg-[#FFF4E6] text-[#17324D]">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 py-10">
        <p className="text-xs tracking-[0.2em] uppercase text-[#D94A3D] font-semibold">Platform admin</p>
        <div className="flex flex-wrap justify-between items-end gap-3">
          <div>
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight">Command Center</h1>
            <p className="text-[#17324D]/80 mt-1">Monitor every restaurant paying for the platform.</p>
          </div>
          <button data-testid="run-dunning-btn" onClick={runDunning} disabled={running}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#17324D] text-white font-semibold rounded-xl hover:bg-[#0F2338] disabled:opacity-60">
            <RefreshCw size={16} className={running ? "animate-spin" : ""} /> Run Dunning Now
          </button>
        </div>

        {stats && stats.past_due_memberships > 0 && (
          <div data-testid="pastdue-alert" className="card-brut rounded-xl p-4 mt-6 flex items-center gap-3 !bg-[#FEF3C7]">
            <AlertTriangle size={20} className="text-[#B45309]" />
            <span className="text-sm font-medium text-[#17324D]">{stats.past_due_memberships} membership(s) are past-due and in the dunning flow (reminders + card-update prompts being sent).</span>
          </div>
        )}

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {[
              { icon: DollarSign, label: "Platform MRR", value: `$${stats.platform_mrr}`, color: "#D94A3D" },
              { icon: TrendingUp, label: "Total Collected", value: `$${stats.platform_revenue}`, color: "#17324D" },
              { icon: Store, label: "Paying Restaurants", value: `${stats.active_owners}/${stats.total_owners}`, color: "#D94A3D" },
              { icon: Utensils, label: "Total Clubs", value: stats.total_clubs, color: "#17324D" },
            ].map((s, i) => (
              <div key={i} data-testid={`admin-stat-${i}`} className="card-brut rounded-xl p-5 bg-white">
                <s.icon size={20} style={{ color: s.color }} />
                <p className="text-xs tracking-[0.15em] uppercase text-[#17324D]/60 mt-2">{s.label}</p>
                <p className="font-heading font-bold text-2xl">{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            <div data-testid="total-customers-card" className="card-brut rounded-xl p-4 text-white !bg-[#17324D] !border-0">
              <Users size={18} className="text-[#D94A3D]" />
              <p className="text-xs uppercase text-white/60 mt-1">Total Customers</p>
              <p className="font-heading font-bold text-xl">{stats.total_customers}</p>
            </div>
            <div data-testid="active-memberships-card" className="card-brut rounded-xl p-4 text-white !bg-[#17324D] !border-0">
              <BadgeCheck size={18} className="text-[#D94A3D]" />
              <p className="text-xs uppercase text-white/60 mt-1">Active Memberships</p>
              <p className="font-heading font-bold text-xl">{stats.active_memberships}</p>
            </div>
            <div data-testid="platform-fee-card" className="card-brut rounded-xl p-4 text-white col-span-2 !bg-[#17324D]">
              <p className="text-xs uppercase text-white/60">Platform Fee</p>
              <p className="font-heading font-bold text-xl text-[#D94A3D]">${stats.platform_fee}/mo per restaurant</p>
            </div>
          </div>
        )}

        {/* Owners table */}
        <h2 className="font-heading font-bold text-2xl tracking-tight mt-12">Restaurants</h2>
        <div className="card-brut rounded-xl bg-white mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FFF4E6] text-left text-xs font-bold uppercase tracking-wider text-[#17324D]/60">
              <tr>
                <th className="p-3">Owner</th><th className="p-3">Email</th>
                <th className="p-3">Platform</th><th className="p-3">Renews</th>
                <th className="p-3">Clubs</th><th className="p-3">Active Members</th>
              </tr>
            </thead>
            <tbody>
              {owners.length === 0 ? (
                <tr><td colSpan="6" className="p-6 text-center text-[#17324D]/60">No restaurants yet.</td></tr>
              ) : owners.map((o) => (
                <tr key={o.id} data-testid={`owner-row-${o.id}`} className="border-t border-[#17324D]/10">
                  <td className="p-3 font-medium">{o.name}</td>
                  <td className="p-3">{o.email}</td>
                  <td className="p-3">
                    {o.platform_active ? (
                      <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-xl bg-[#17324D] text-white"><BadgeCheck size={12} /> ACTIVE</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-xl bg-[#D94A3D] text-white"><XCircle size={12} /> INACTIVE</span>
                    )}
                  </td>
                  <td className="p-3">{o.platform_period_end ? new Date(o.platform_period_end).toLocaleDateString() : "—"}</td>
                  <td className="p-3">{o.clubs}</td>
                  <td className="p-3">{o.active_members}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Platform revenue events */}
        <h2 className="font-heading font-bold text-2xl tracking-tight mt-12">Platform Payments</h2>
        <div className="mt-4 space-y-2">
          {events.length === 0 ? <p className="text-[#17324D]/60">No platform payments yet.</p> :
            events.map((ev) => (
              <div key={ev.id} data-testid={`platform-event-${ev.id}`} className="card-brut rounded-xl p-3 bg-white flex justify-between items-center">
                <span className="text-sm"><span className="font-bold text-[#17324D] mr-2">{ev.owner_name}</span>{ev.message}</span>
                <span className="text-xs text-[#17324D]/50">{new Date(ev.created_at).toLocaleString()}</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
